import { task, Unit, type Task } from 'true-myth';
import { isDev } from '@zg-rentals/environment-utils';
import { logError } from '@zg-rentals/log-error';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { ChainedError } from '@zg-rentals/stdlib/task';
import { identify } from '@zillow/universal-analytics';
import { fetchEncodedZuid } from './fetchEncodedZuid';
import { getAnalyticsErrorType, getCountName, isAnalyticsDisabled } from './utils';

const identifyAs = (userId: string | null): Task<Unit, ChainedError> =>
  task
    .tryOrElse(
      (cause) => ChainedError.of('identify failed', cause),
      async () => {
        identify(userId || '');
        return Unit;
      },
    )
    .inspectRejected((error) => logError({ error, errorType: getAnalyticsErrorType('IdentifyUserError') }));

/**
 * Identifies the clickstream user. Some non-Zillow brands pass a user ID explicitly, and an explicit `null` means
 * logged out. With no argument, the Zillow user is looked up from page frame's client-data; if that lookup fails,
 * nothing is identified, so the SDK keeps whatever user it already has (the host's, or the zjs_user_id value it read
 * on load).
 */
export function identifyUser(userId?: string | null): Task<Unit, ChainedError> {
  if (userId !== undefined) {
    return identifyAs(userId);
  }

  // local dev doesn't proxy page frame's /pfs routes, and the SDK no-ops while analytics is disabled
  if (typeof window === 'undefined' || isDev() || isAnalyticsDisabled()) {
    return task.resolve(Unit);
  }

  return fetchEncodedZuid()
    .inspect(() => getGlobalMonitor()?.count({ name: getCountName('identifyUser clientData') }))
    .inspectRejected((error) => {
      getGlobalMonitor()?.count({ name: getCountName('identifyUser clientDataFailed') });
      logError({ error, errorType: getAnalyticsErrorType('IdentifyUserClientDataError') });
    })
    .andThen((encodedZuid) => identifyAs(encodedZuid.unwrapOr(null)));
}
